/**
 * 1:1 port of packages/amgbandarqq_v2/lib/domain/game_engine/result_parser.dart.
 *
 * Result-string format:
 *   "a|b,c|d,e|f,g|h,#redScore,#blueScore,@291,294,..[,NeedShuffle]"
 *
 *   - 4 domino tiles encoded as 4 pip pairs "a|b" (8 pips total).
 *   - First 2 tiles → red (player). Last 2 tiles → blue (banker).
 *   - "#<score>" appears twice; first is red, second is blue.
 *   - "@id,id,..." is the CSV of winning bet-item IDs.
 *   - "NeedShuffle" optional flag.
 *
 * The parser converts each (a,b) pair into a sprite index 0..27 via
 * the canonical domino-suit ordering below.
 */
import type { BetItemId, GameResult } from '../types/game';
import { isBetItemId } from '../types/game';

export interface ParsedResult {
  playerScore: number;
  bankerScore: number;
  winner: GameResult;
  winningBetIds: BetItemId[];
  needsShuffle: boolean;
  /** Length 4: [redA, redB, blueA, blueB] sprite indices. */
  dominoSpriteIndices: number[];
}

/** Canonical domino-pair → sprite-index mapping (result_parser.dart:97-126). */
const DOMINO_MAP: Record<string, number> = {
  '0|0': 0,
  '0|1': 1,
  '1|1': 2,
  '0|2': 3,
  '1|2': 4,
  '2|2': 5,
  '0|3': 6,
  '1|3': 7,
  '2|3': 8,
  '3|3': 9,
  '0|4': 10,
  '1|4': 11,
  '2|4': 12,
  '3|4': 13,
  '4|4': 14,
  '0|5': 15,
  '1|5': 16,
  '2|5': 17,
  '3|5': 18,
  '4|5': 19,
  '5|5': 20,
  '0|6': 21,
  '1|6': 22,
  '2|6': 23,
  '3|6': 24,
  '4|6': 25,
  '5|6': 26,
  '6|6': 27,
};

function mapDominoValue(first: number, second: number): number {
  const a = Math.min(first, second);
  const b = Math.max(first, second);
  const idx = DOMINO_MAP[`${a}|${b}`];
  return Number.isFinite(idx) ? idx : 0;
}

export function parseResultString(raw: string): ParsedResult {
  let redScore: number | null = null;
  let blueScore: number | null = null;
  let winningBetItems = '';
  let needsShuffle = false;

  const redDominoPips: number[] = [];
  const blueDominoPips: number[] = [];

  const parts = raw.split(',').map((s) => s.trim());

  for (const part of parts) {
    if (part.length === 0) continue;

    if (part === 'NeedShuffle') {
      needsShuffle = true;
      continue;
    }

    if (part.startsWith('#')) {
      const score = parseInt(part.substring(1), 10);
      if (Number.isFinite(score)) {
        if (redScore === null) redScore = score;
        else blueScore = score;
      }
      continue;
    }

    if (part.startsWith('@')) {
      winningBetItems = part.substring(1);
      continue;
    }

    // Domino tile "a|b" — card-like ("clubs-5") fragments contain '-' and
    // are intentionally ignored here.
    if (part.includes('|') && !part.includes('-')) {
      const pair = part.split('|').map((s) => s.trim());
      if (pair.length === 2) {
        const a = parseInt(pair[0], 10);
        const b = parseInt(pair[1], 10);
        if (Number.isFinite(a) && Number.isFinite(b)) {
          if (redDominoPips.length < 4) {
            redDominoPips.push(a, b);
          } else {
            blueDominoPips.push(a, b);
          }
        }
      }
    }
  }

  const resolvedRed = redScore ?? 0;
  const resolvedBlue = blueScore ?? 0;
  const winner: GameResult =
    resolvedRed > resolvedBlue
      ? 'playerWin'
      : resolvedBlue > resolvedRed
      ? 'bankerWin'
      : 'tie';

  const winningBetIds: BetItemId[] = [];
  if (winningBetItems.length > 0) {
    for (const tok of winningBetItems.split(',')) {
      const n = parseInt(tok.trim(), 10);
      if (Number.isFinite(n) && isBetItemId(n)) winningBetIds.push(n);
    }
  }

  const r = [...redDominoPips];
  const b = [...blueDominoPips];
  while (r.length < 4) r.push(0);
  while (b.length < 4) b.push(0);

  return {
    playerScore: resolvedRed,
    bankerScore: resolvedBlue,
    winner,
    winningBetIds,
    needsShuffle,
    dominoSpriteIndices: [
      mapDominoValue(r[0], r[1]),
      mapDominoValue(r[2], r[3]),
      mapDominoValue(b[0], b[1]),
      mapDominoValue(b[2], b[3]),
    ],
  };
}
